import React, { useEffect, useCallback } from 'react';
import QuoteContext, { useQuoteContext } from '@/dataroom/app/landing/QuoteContext';
import Button, { ButtonVariantType } from '@dealroadshow/uikit-button';
import FinalForm, { Field } from '@framework/ui/Organisms/FinalForm';
import ReCaptcha from '@framework/ui/Molecules/Form/ReCaptcha';
import Spinner from '@dealroadshow/uikit-loader/Spinner';
import PhoneInput from '@dealroadshow/uikit-input/PhoneInput';
import Input from '@dealroadshow/uikit-input';
import { CheckboxInput } from '@dealroadshow/uikit-checkbox';
import { Select } from '@framework/ui/Organisms/FinalForm/Fields/Select';
import CurrenciesSelect from '@framework/ui/Pages/Landing/Sections/Form/CurrenciesSelect';
import QuoteFormSuccess from './QuoteFormSuccess';
import validate from './validator';
import spacesStyles from '@dealroadshow/uikit-styles/helpers/spaces.module.scss';
import styles from './quoteForm.module.scss';
import cn from 'classnames';
import baseStyles from '@/dataroom/ui/components/Landing/dealvdr/tenantDealvdrStyles.module.scss';
import { TFinalFormRenderProp } from '@framework/ui/Organisms/FinalForm/interfaces';
import { IDealVdrFormData } from '@/dataroom/app/landing/useGetQuote';

const QuoteForm = () => {
  const {
    isFetching,
    currenciesCollection,
    periodsCollection,
    sizesCollection,
    currency,
    defaultPeriodId,
    defaultSizeId,
    isSuccessScreen,
    getQuote,
  } = useQuoteContext();

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [isSuccessScreen]);

  const initialValues = {
    currency: currency?.value,
    period: defaultPeriodId,
    size: defaultSizeId,
  };

  const renderForm = useCallback<TFinalFormRenderProp<IDealVdrFormData>>(() => (
    <>
      <div className={ cn(styles.row, styles.firstRow) }>
        <div className={ styles.estimatedDuration }>
          <Field
            name="period"
            label="Estimated Duration"
            component={ Select }
            options={ periodsCollection }
            isDisabled={ isFetching }
            isSimpleValue
            dataTest="durationQuoteFormReduxFormSelect"
          />
        </div>
        <div className={ styles.estimatedSizeWrp }>
          <Field
            name="size"
            label="Estimated Size"
            component={ Select }
            options={ sizesCollection }
            isDisabled={ isFetching }
            isSimpleValue
            dataTest="sizeOfQuoteFormReduxFormSelect"
          />
        </div>
        <div className={ styles.currencyWrp }>
          <Field
            name="currency"
            component={ ({ input }) => (
              <CurrenciesSelect
                label="Currency"
                options={ currenciesCollection }
                isDisabled={ isFetching }
                value={ input.value }
                onChange={ (selectedCurrency) => input.onChange(selectedCurrency.value) }
              />
            ) }
          />
        </div>
      </div>
      <Field
        name="company"
        label="Company"
        placeholder="Company Name"
        component={ Input }
        disabled={ isFetching }
        dataTest="quoteCompanyNameInput"
      />
      <div className={ styles.row }>
        <div className={ styles.input }>
          <Field
            name="firstName"
            label="First name"
            placeholder="First Name"
            component={ Input }
            disabled={ isFetching }
            dataTest="quoteFirstNameInput"
          />
        </div>
        <div className={ styles.input }>
          <Field
            name="lastName"
            label="Last name"
            placeholder="Last Name"
            component={ Input }
            disabled={ isFetching }
            dataTest="quoteLastNameInput"
          />
        </div>
      </div>
      <div className={ styles.row }>
        <div className={ styles.input }>
          <Field
            name="email"
            label="Corporate email"
            placeholder="Email Address"
            component={ Input }
            disabled={ isFetching }
            dataTest="quoteEmailAddressInput"
          />
        </div>
        <div className={ styles.input }>
          <Field
            name="phone"
            label="Phone Number"
            placeholder="Phone"
            isFinalForm
            component={ PhoneInput }
            disabled={ isFetching }
            dataTest="quoteFormInputPhone"
          />
        </div>
      </div>
      <Field
        name="callback"
        label="Request a call back from a DealVDR specialist?"
        type="checkbox"
        className={ styles.checkbox }
        component={ CheckboxInput }
        isErrorMessage={ false }
        disabled={ isFetching }
        dataTest="requestCallCheckbox"
      />
      <div className={ styles.recaptchaWrp }>
        <Field
          name="recaptcha"
          component={ ReCaptcha }
        />
      </div>
      <div className={ styles.buttonWrp }>
        <Button
          variant={ ButtonVariantType.action }
          className={ cn(baseStyles.actionButton, styles.generateQuoteButton, spacesStyles.mrn) }
          type="submit"
          title="Generate Quote"
          dataTest="quoteFormSubmitButton"
        />
      </div>
    </>
  ), [
    isFetching,
    currenciesCollection,
    periodsCollection,
    sizesCollection,
  ]);

  if (isSuccessScreen) {
    return <QuoteFormSuccess />;
  }

  return (
    <div className={ styles.form }>
      <Spinner
        isVisible={ isFetching }
        overlay
      />
      <FinalForm<IDealVdrFormData>
        dataTest="getQuoteForm"
        initialValues={ initialValues }
        onSubmit={ getQuote }
        render={ renderForm }
        validate={ validate }
      />
    </div>
  );
};

export default () => (
  <QuoteContext>
    <QuoteForm />
  </QuoteContext>
);
